import React, { useEffect, useState } from "react";
import { getData } from "../utilities/UtilityFunctions";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Grid,
  LinearProgress,
  Typography,
  useTheme
} from "@mui/material";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import {
  armyListActions,
  userDisplayName
} from "../../pages/events/components/EventFunctions";
import { BCPAngleRight } from "../icons/bcp/BCPIcons";

const IS_GW =
  getEnvironmentVariable("IS_GW") === "true" ||
  getEnvironmentVariable("COMPANY_FLAG") === "gw";

export default function EventLeaderboardCard(props) {
  const {
    disablePhotoFetch = true,
    buttonLabel,
    region,
    regionId,
    onClick,
    event,
    href
  } = props;

  const limit = props?.limit || 3;
  const [photo, setPhoto] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const theme = useTheme();

  const fetchLeaderboard = async () => {
    try {
      if (!event) return;

      setSubLoading(true);

      const apiLeaderboard = await getData(`/events/${event?.id}/players`, {
        limit: limit || 3,
        placings: true
      });

      setLeaderboard(apiLeaderboard?.active?.splice(0, limit || 3) || []);
    } catch (err) {
    } finally {
      setSubLoading(false);
    }
  };

  const fetchPhoto = async () => {
    try {
      const apiPhoto = await getData(`/files/${event?.photoFileId}`);
      if (apiPhoto) {
        setPhoto(apiPhoto);
      }
    } catch {}
  };

  useEffect(() => {
    if (event) {
      // If no leaderboards, fetch them and add them to the ranking object
      fetchLeaderboard();
    }

    if (event?.photoFileId && !disablePhotoFetch) fetchPhoto();
    // eslint-disable-next-line
  }, [region, regionId]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
      }}
    >
      <CardActionArea
        onClick={onClick}
        href={
          onClick ? "" : href ? href : `/event/${event.id}?active_tab=placings`
        }
        target="_blank"
      >
        <CardMedia
          component="img"
          image={
            event?.photo?.url ||
            event?.photoUrl ||
            photo?.url ||
            event?.imageUrl ||
            event?.gameSystem?.url ||
            `${getEnvironmentVariable(
              "SYSTEM_IMAGES_CLOUDFRONT"
            )}/images/EventImage${
              theme?.palette?.mode === "dark" ? "Dark" : ""
            }.png`
          }
          alt={event?.name}
          sx={{
            backgroundColor: "#222222",
            maxHeight: "calc((100vw - 24px) * 0.5625)",
            "@media (min-width: 600px)": {
              maxHeight: "calc((100vw - 24px) * 0.5 * 0.5625)"
            },
            "@media (min-width: 900px)": {
              maxHeight: "calc((100vw - 24px) * 0.3333 * 0.5625)"
            },
            "@media (min-width: 1218px)": {
              maxHeight: "215px"
            }
          }}
        />

        <CardHeader
          title={
            <>
              <Typography
                variant="h4"
                s
                sx={{
                  m: 0,
                  fontWeight: "700",
                  fontSize: "20px",
                  lineHeight: "1.334"
                }}
              >
                {event?.name}
              </Typography>
              <Typography
                gutterBottom={true}
                sx={{
                  fontWeight: "400",
                  fontSize: "18px",
                  lineHeight: "1.334"
                }}
              >
                {event?.gameSystem?.name}
              </Typography>
            </>
          }
        />
      </CardActionArea>

      <CardContent>
        <Grid container spacing={1}>
          {/* Loop through rankings here */}
          {event?.hidePlacings ? (
            <Grid item xs={12}>
              <Typography>Rankings are hidden</Typography>
            </Grid>
          ) : (
            <>
              {subLoading ? (
                <Grid item xs={12}>
                  <LinearProgress />
                </Grid>
              ) : leaderboard?.length ? (
                leaderboard?.map((player, index) => {
                  let _armyListActions = armyListActions(player, event, false);

                  return (
                    <CardActionArea
                      {..._armyListActions}
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        pt: 0.5,
                        pb: 0.5,
                        gap: 1,
                        borderRadius: IS_GW ? 0 : 1,
                        backgroundColor: IS_GW
                          ? index % 2 === 0
                            ? "table.evenRowBackground"
                            : "table.oddRowBackground"
                          : "inherit",
                        borderBottom: IS_GW ? "1px solid" : "none",
                        borderTop: IS_GW && index === 0 ? "1px solid" : "none"
                      }}
                      key={player?.id || index}
                      target="_blank"
                    >
                      <Grid item xs={2} sx={{ textAlign: "center", pl: 1 }}>
                        <Typography>{player?.placing}</Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          sx={{
                            color:
                              player?.id && !IS_GW ? "link.main" : "inherit",
                            overflow: "hidden"
                          }}
                        >
                          {userDisplayName(
                            player?.user?.firstName,
                            player?.user?.lastName
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography sx={{ pr: 1 }}>
                          {player?.faction?.name &&
                            !event.hideLists &&
                            event?.gameSystem?.hasFactions && (
                              <Typography variant="body2" gutterBottom>
                                {player?.faction?.name}
                              </Typography>
                            )}
                        </Typography>
                      </Grid>
                    </CardActionArea>
                  );
                })
              ) : (
                <Grid item xs={12}>
                  <Typography>No rankings available</Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </CardContent>

      {IS_GW ? (
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 1
          }}
        >
          <Button
            onClick={onClick}
            href={
              onClick
                ? ""
                : href
                  ? href
                  : `/event/${event.id}?active_tab=placings`
            }
            target="_blank"
            variant="contained"
            endIcon={<BCPAngleRight sx={{ fontSize: "14px", width: "14px" }} />}
            sx={{ borderRadius: 50 }}
          >
            Complete Leaders
          </Button>
        </Grid>
      ) : (
        <CardActionArea
          onClick={onClick}
          href={
            onClick
              ? ""
              : href
                ? href
                : `/event/${event.id}?active_tab=placings`
          }
          target="_blank"
        >
          <CardActions>
            <Button
              fullWidth
              sx={{
                color: "link.main",
                textTransform: "none"
              }}
            >
              {buttonLabel || "View complete leaderboard..."}
            </Button>
          </CardActions>
        </CardActionArea>
      )}
    </Card>
  );
}
